import React from 'react'
import { Box, Typography, alpha } from '@mui/material'

import type { HolidayImage } from '@/src/types/holidays'

import HolidayImageView from '../holidayImage'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface ImageCarouselProps {
    images: HolidayImage[]
    activeIndex: number
    trackRef: React.RefObject<HTMLDivElement>
    onScroll: () => void
    onSelect: (index: number) => void
    height: number
}

/**
 * A swipeable, snapping track. Stacking a day's images vertically made one day of an itinerary
 * taller than a phone screen three times over, which pushed the next day out of reach; here they
 * occupy one slot and advance on their own.
 *
 * The counter and dots sit on the photo rather than under it, over a scrim dark enough to keep
 * them legible whatever the image behind them looks like.
 */
const ImageCarousel: React.FC<ImageCarouselProps> = ({
    images,
    activeIndex,
    trackRef,
    onScroll,
    onSelect,
    height
}) => {
    const hasMany = images.length > 1

    return (
        <Box
            sx={{
                position: 'relative',
                height,
                borderRadius: HOLIDAYS_RADIUS.NESTED,
                overflow: 'hidden',
                bgcolor: 'neutral.100'
            }}
        >
            <Box
                ref={trackRef}
                onScroll={onScroll}
                sx={{
                    display: 'flex',
                    height: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollSnapType: 'x mandatory',
                    // The track is driven by swipe and by the timer, so its own scrollbar is noise.
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' }
                }}
            >
                {images.map(image => (
                    <Box key={image.url} sx={{ flex: '0 0 100%', height: '100%', scrollSnapAlign: 'start' }}>
                        <HolidayImageView image={image} isMobile />
                    </Box>
                ))}
            </Box>

            {hasMany && (
                <>
                    <Typography
                        sx={theme => ({
                            position: 'absolute',
                            top: 8,
                            insetInlineEnd: 8,
                            px: 0.875,
                            py: 0.25,
                            borderRadius: 50,
                            fontSize: '0.688rem',
                            fontWeight: 700,
                            color: 'common.white',
                            bgcolor: alpha(theme.palette.common.black, 0.55),
                            pointerEvents: 'none'
                        })}
                    >
                        {activeIndex + 1}/{images.length}
                    </Typography>

                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            insetInline: 0,
                            bottom: 0,
                            height: 56,
                            pointerEvents: 'none',
                            background: `linear-gradient(to top, ${alpha(
                                theme.palette.common.black,
                                0.55
                            )}, ${alpha(theme.palette.common.black, 0)})`
                        })}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            insetInline: 0,
                            bottom: 10,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 0.75
                        }}
                    >
                        {images.map((image, index) => (
                            <Box
                                key={image.url}
                                component="button"
                                type="button"
                                aria-label={image.alt || undefined}
                                aria-current={index === activeIndex}
                                onClick={() => onSelect(index)}
                                sx={theme => ({
                                    width: index === activeIndex ? 18 : 6,
                                    height: 6,
                                    p: 0,
                                    border: 'none',
                                    borderRadius: 50,
                                    cursor: 'pointer',
                                    transition: 'width 0.3s, background-color 0.3s',
                                    bgcolor:
                                        index === activeIndex
                                            ? 'common.white'
                                            : alpha(theme.palette.common.white, 0.5)
                                })}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    )
}

export { ImageCarousel }
export default ImageCarousel
